// Builds the soil legend from the soil types actually present in the open
// field, rather than printing a provider's whole national key.
//
// Why zone-scoped: England and Wales Soilscapes has 29 published classes and
// Scotland 11. A farmer looking at one field meets a handful, so a national key
// is a wall of colours that mostly are not on their screen. Measured against a
// Shropshire bbox on 20/08/2026: 7 classes. Davin's call, 20/08: "only show the
// legend items for the areas that are in the zone, no matter how many there
// are. it will never be many."
//
// Colours come from `soilClassColours.js`, generated from each provider's own
// `/legend?f=json`, so a swatch cannot drift away from the painted tile. That
// is the same rule the rest of the legend follows.
//
// This module is pure. Fetching is the caller's job.

import { SOILSCAPES, HUTTON_GEN_SOIL_TYPE } from '../data/soilClassColours';
import { shortSoilClassName } from '../data/soilClassNames';
import { layerStatusLine } from './layerStatusCopy';

// All farmer-facing copy for the soil legend lives here. Confirmed 11/09/2026.
//
// "Zone" rather than "field", and "Click" rather than "Open": zone is the app's
// own word for the thing a farmer draws, and clicking one is what actually
// happens (Davin, 11/09/2026).
export const STRINGS = {
  label: 'Soil type',
  noZone: 'Click a zone to see the soil types in it.',
  noneFound: 'No soil information found for this zone.',
  // Soil joins the same "Learn more about <thing>" pattern as every other
  // legend card (Davin, 11/09/2026). It used to be the odd one out twice over:
  // England and Wales said "See the full soil key" and Scotland said "Guide to
  // Scotland's soil types", so the same card was worded differently by nation
  // and differently again from the other ten source links.
  //
  // Named by the dataset rather than by the layer, because "Learn more about
  // soil type" would say nothing a farmer does not already know.
  fullKeyLink: 'Learn more about Soilscapes',
  soilGuideLink: 'Learn more about Scottish Soil',
  // The nearby tier had a heading here, "Around the field". REMOVED 11/09/2026
  // on Davin's call, told first that the styling alone would then carry the
  // distinction: nearby swatches keep their own `swatchRowNearby` treatment, so
  // they still LOOK different, but nothing now says they are up to 250 m outside
  // the zone.
};

// Where a farmer goes for the whole classification, per provider. Both were
// wrong and were corrected on 20/08/2026:
//
//   England and Wales pointed at `services/soilsguide/soilscapes.cfm`, which
//   with no ssid parameter serves ONE soil type (Saltmarsh soils, class 1), not
//   the key. `soilsguide/soilscapes_list.cfm` is the actual list, 27 classes,
//   each linking to its own description.
//
//   Scotland pointed at the national soil map page, which is the map source
//   rather than the classification. The Scottish Government's guide to soil
//   types is what a farmer reading "brown soils" or "peaty podzols" needs.
//
// Both verified to return 200 on 20/08/2026.
const FULL_KEY_URL = {
  england: 'https://www.landis.org.uk/soilsguide/soilscapes_list.cfm',
  wales: 'https://www.landis.org.uk/soilsguide/soilscapes_list.cfm',
  scotland: 'https://soils.environment.gov.scot/soils-in-scotland/guide-to-soil-types/',
};

// Scotland reads a different dataset from England and Wales, so the link names
// a different thing.
const LINK_TEXT = {
  scotland: STRINGS.soilGuideLink,
};

const COLOURS_BY_REGION = {
  england: SOILSCAPES,
  wales: SOILSCAPES,
  scotland: HUTTON_GEN_SOIL_TYPE,
};

// Providers render a few classes they do not publish in their legend: England
// paints a "water" class that is absent from the 29 published entries. Falling
// back to a neutral swatch keeps the class named and visible rather than
// dropping it, which would tell the farmer the field has less in it than it
// does.
const UNPUBLISHED_CLASS_COLOUR = '#C9C9C9';

// Classes covering less than this fraction of the field are dropped. A sliver
// of a neighbouring soil type clipped by the field boundary is usually a survey
// edge at 1:250,000 rather than something a farmer needs told about.
//
// The comparison is `>=`, so a class sitting exactly on the threshold is KEPT.
// Exported so the boundary tests read the real constant instead of a copy of
// its value that can drift away from it (BBB-525).
export const MIN_AREA_FRACTION = 0.005;

function lookupColour(table, value) {
  if (!table || value == null) return null;
  if (Object.prototype.hasOwnProperty.call(table, value)) return table[value];
  // Provider casing is not guaranteed stable ("water" vs "Water body"), so try
  // a case-insensitive pass before giving up on a published colour.
  const wanted = String(value).toLowerCase();
  for (const key of Object.keys(table)) {
    if (key.toLowerCase() === wanted) return table[key];
  }
  return null;
}

/**
 * Collect the soil classes present in a field, largest share of the field
 * first.
 *
 * @param {object|null} zoneLandData result from getZoneLandData. Its `layers` is
 *   an object keyed by layer id, not an array.
 * @returns {Array<{value: string, areaFraction: number}>} may be empty
 */
function collectClasses(layers, key) {
  const byValue = new Map();
  for (const layer of Object.values(layers)) {
    if (layer?.category !== 'soil' || layer.status !== 'ok') continue;
    for (const entry of layer[key] || []) {
      if (!entry || entry.value == null) continue;
      // Two soil layers can cover one field (Scotland carries a 250k and a 25k
      // source). Keep the larger reading rather than summing, which would claim
      // more than 100% of the field.
      const existing = byValue.get(entry.value);
      if (!existing || entry.areaFraction > existing.areaFraction) {
        byValue.set(entry.value, entry);
      }
    }
  }

  return [...byValue.values()]
    .filter((entry) => entry.areaFraction >= MIN_AREA_FRACTION)
    .sort((a, b) => b.areaFraction - a.areaFraction);
}

export function soilClassesInZone(zoneLandData) {
  const layers = zoneLandData?.layers;
  if (!layers || typeof layers !== 'object') return [];
  return collectClasses(layers, 'legendClasses');
}

/**
 * Collect the soil classes found in the scanned area around the field but NOT in
 * the field itself, largest share of the SCANNED AREA first.
 *
 * The fractions on these entries are fractions of the grown rectangle, not of
 * the field, so MIN_AREA_FRACTION here drops a sliver of the scan rather than a
 * sliver of the field. That is the intended reading: the threshold means "too
 * small to be worth naming in whatever area this tier is about".
 *
 * @param {object|null} zoneLandData result from getZoneLandData
 * @returns {Array<{value: string, areaFraction: number}>} may be empty
 */
export function soilClassesNearZone(zoneLandData) {
  const layers = zoneLandData?.layers;
  if (!layers || typeof layers !== 'object') return [];

  const inField = new Set(soilClassesInZone(zoneLandData).map((entry) => entry.value));
  // A class in the field is already named above; repeating it under "around the
  // field" would read as two different soils.
  return collectClasses(layers, 'nearbyLegendClasses').filter(
    (entry) => !inField.has(entry.value)
  );
}

/**
 * Build the soil legend card.
 *
 * Returns a card in every state rather than null, because the soil layer being
 * on and the legend saying nothing at all is the state this replaces.
 *
 * @param {object|null} zoneLandData result from getZoneLandData, or null when no
 *   field is open, the fetch has not landed, or the fetch failed
 * @param {string|null} region 'england' | 'scotland' | 'wales'
 * @param {string} [status] the fetch status from useZoneSoilLegend. Without it a
 *   null zoneLandData reads as "no field is open", which is what it used to mean
 *   for all three of nothing-open, still-loading and failed. Optional so a
 *   caller that genuinely only has the no-field case keeps working.
 * @returns {{sourceId: string, label: string, swatches: Array, caption?: string,
 *            link?: {href: string, text: string}}}
 */
export function buildSoilLegend(zoneLandData, region, status) {
  const fullKeyUrl = FULL_KEY_URL[region] || FULL_KEY_URL.england;
  const link = { href: fullKeyUrl, text: LINK_TEXT[region] || STRINGS.fullKeyLink };
  const card = (caption) => ({
    sourceId: 'soil',
    label: STRINGS.label,
    swatches: [],
    caption,
    link,
  });

  // Progress and failure read from layerStatusCopy rather than from strings of
  // this card's own, so a farmer sees the same two lines here as on the layer
  // toggles and they can be changed in one pass. No new farmer-facing copy.
  if (status === 'loading') return card(layerStatusLine('loading'));
  if (status === 'unavailable') return card(layerStatusLine('unavailable'));

  if (!zoneLandData) {
    // A landed fetch that yielded nothing is an answer about this field, not an
    // invitation to open one.
    return card(status === 'loaded' ? STRINGS.noneFound : STRINGS.noZone);
  }

  const classes = soilClassesInZone(zoneLandData);
  const nearby = soilClassesNearZone(zoneLandData);

  if (classes.length === 0 && nearby.length === 0) {
    return {
      sourceId: 'soil',
      label: STRINGS.label,
      swatches: [],
      caption: STRINGS.noneFound,
      link,
    };
  }

  const table = COLOURS_BY_REGION[region] || null;
  // Short name on the legend; the provider's full class name and a link to their
  // description of that exact class stay on the Zone Details row, so nothing is
  // lost by shortening. See data/soilClassNames.js.
  const toSwatch = (entry, isNearby) => ({
    colour: lookupColour(table, entry.value) || UNPUBLISHED_CLASS_COLOUR,
    label: shortSoilClassName(entry.value),
    // The renderer marks these off under their own heading. A nearby class is
    // not in the farmer's field and must never be read as if it were.
    ...(isNearby ? { nearby: true } : {}),
  });

  const swatches = [
    // In the field first, largest share first: the order Davin specified 20/08.
    ...classes.map((entry) => toSwatch(entry, false)),
    ...nearby.map((entry) => toSwatch(entry, true)),
  ];

  return {
    sourceId: 'soil',
    label: STRINGS.label,
    swatches,
    // A field with one soil type must still name it, rather than collapsing to
    // a swatch captioned "Soil type" the way the single-swatch layers do.
    alwaysLabelSwatches: true,
    link,
  };
}
